import { ArrowDownRight, ArrowUpRight, BookCopy, Crown, Filter, Loader2, RefreshCw, ShieldAlert, X } from 'lucide-react';
import { useCallback, useEffect, useId, useState } from 'react';
import { toast } from 'sonner';
import { listAdminAudit } from '@/api/admin';
import AdminDateRange from '@/components/admin/AdminDateRange';
import AdminPagination from '@/components/admin/AdminPagination';

// Ledger type dropdown values.
//
// "Admin" is a synthetic filter (backend maps it to
// InternalTransfer + metadata.purpose="admin_balance_adjustment"). It surfaces
// manual purchase/withdrawal balance edits performed by admins via the User
// Directory. All other values are real LedgerType enum members.
//
// GOD-ADMIN ONLY: the "Admin" option is added dynamically inside the component
// based on the caller's is_god_account flag. Delegated (sub) admins never see
// the option nor the actor attribution — mirroring the backend which returns
// HTTP 403 for ?type=Admin and strips actor metadata for non-god callers.
const BASE_LEDGER_TYPES = [
  '',
  'Deposit',
  'Withdrawal',
  'Cycle',
  'Match',
  'PIF',
  'InternalTransfer',
  'SelfWalletExchange',
  'DepositCorrection',
  'MatchCorrection',
];

const money = (raw) => {
  const n = Number(raw ?? 0);
  const sign = n < 0 ? '-' : '+';
  return `${sign}$${Math.abs(n).toFixed(2)}`;
};

const typeStyle = (t) => {
  if (t === 'Withdrawal' || t === 'InternalTransfer') return { color: '#fca5a5', border: '1px solid rgba(248,113,113,0.4)' };
  if (t === 'Deposit' || t === 'Cycle' || t === 'Match' || t === 'PIF') return { color: '#FFE08A', border: '1px solid rgba(255,182,18,0.45)' };
  return { color: '#FFC20E', border: '1px solid rgba(255,182,18,0.32)' };
};

// Detect whether a ledger row is a manual admin balance adjustment.
// This mirrors the backend synthetic-Admin filter contract exactly.
const isAdminAdjustment = (row) => row?.type === 'InternalTransfer' && row?.metadata?.purpose === 'admin_balance_adjustment';

// Human-friendly bucket label for admin adjustment rows.
const bucketLabel = (bucket) => {
  if (bucket === 'purchase_balance') return 'Purchase';
  if (bucket === 'withdrawal_balance') return 'Withdrawal';
  return bucket || '—';
};

const AuditSubpage = ({ admin }) => {
  const uid = useId();
  // Caller privilege gate. Delegated admins do NOT see the "Admin" filter
  // option, nor the actor-attribution badges — even if the backend
  // accidentally leaked the marker, the UI would still hide it here.
  const isGod = !!admin?.is_god_account;
  const ledgerTypes = isGod ? [...BASE_LEDGER_TYPES, 'Admin', 'UserToUser'] : BASE_LEDGER_TYPES;

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    user_id: '',
    type: '',
    date_from: '',
    date_to: '',
    hash: '',
    q: '',
  });
  const [data, setData] = useState({ items: [], page: 1, total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page };
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const d = await listAdminAudit(params);
      setData(d);
    } catch {
      toast.error('Failed to load audit');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    const t = setTimeout(load, 220);
    return () => clearTimeout(t);
  }, [load]);

  const reset = () => {
    setFilters({ user_id: '', type: '', date_from: '', date_to: '', hash: '', q: '' });
    setPage(1);
  };

  const setFilter = (k, v) => {
    setPage(1);
    setFilters((f) => ({ ...f, [k]: v }));
  };

  return (
    <div className="space-y-6" data-testid="audit-subpage">
      <div className="sc-glass rounded-2xl p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-display font-bold text-xl flex items-center gap-2">
              <BookCopy className="w-5 h-5 text-[#FFC20E]" />
              Global Financial Audit Center
            </div>
            <p className="text-xs text-[var(--sc-text-dim)] mt-1 max-w-3xl">
              Master chronological record of every systemic financial event. Filter by user, type, date range, or blockchain hash. 50 records per page
              (server-paginated).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={reset} className="sc-btn-ghost" style={{ padding: '10px 14px', fontSize: 11 }} data-testid="audit-reset">
              <X className="w-3.5 h-3.5" /> Reset
            </button>
            <button type="button" onClick={load} className="sc-btn-ghost" style={{ padding: '10px 14px', fontSize: 11 }} data-testid="audit-refresh">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
          <div className="md:col-span-2">
            <label htmlFor={`${uid}-user`} className="block text-[10px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] mb-1.5">
              User (id or username)
            </label>
            <input
              id={`${uid}-user`}
              className="sc-input"
              value={filters.user_id}
              onChange={(e) => setFilter('user_id', e.target.value)}
              placeholder="e.g. boss"
              data-testid="audit-filter-user"
            />
          </div>
          <div>
            <label htmlFor={`${uid}-type`} className="block text-[10px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] mb-1.5">
              Type
            </label>
            <select
              id={`${uid}-type`}
              className="sc-input"
              value={filters.type}
              onChange={(e) => setFilter('type', e.target.value)}
              data-testid="audit-filter-type"
            >
              {ledgerTypes.map((t) => (
                <option key={t || 'all'} value={t}>
                  {t === '' ? 'All types' : t === 'Admin' ? 'Admin (manual balance edits)' : t === 'UserToUser' ? 'User-to-User (external transfers)' : t}
                </option>
              ))}
            </select>
          </div>
          <AdminDateRange
            dateFrom={filters.date_from}
            dateTo={filters.date_to}
            onChange={(range) => {
              setPage(1);
              setFilters((f) => ({ ...f, ...range }));
            }}
            testIdPrefix="audit-filter"
          />
          <div>
            <label htmlFor={`${uid}-hash`} className="block text-[10px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] mb-1.5">
              Blockchain hash
            </label>
            <input
              id={`${uid}-hash`}
              className="sc-input"
              value={filters.hash}
              onChange={(e) => setFilter('hash', e.target.value)}
              placeholder="sha-256 fragment…"
              data-testid="audit-filter-hash"
            />
          </div>
        </div>
      </div>

      <div className="sc-glass rounded-2xl p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] flex items-center gap-2">
            <Filter className="w-3.5 h-3.5" />
            {data.total} records
          </div>
        </div>
        <div className="sc-admin-table-wrap">
          <table className="sc-admin-table" data-testid="audit-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>User</th>
                <th>Type</th>
                <th className="text-right">Amount</th>
                <th>Tx Hash</th>
              </tr>
            </thead>
            <tbody>
              {loading && data.items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12">
                    <Loader2 className="w-5 h-5 mx-auto animate-spin text-[#FFB612]" />
                  </td>
                </tr>
              ) : data.items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-[var(--sc-text-mute)]">
                    No ledger entries match these filters.
                  </td>
                </tr>
              ) : (
                data.items.map((row) => {
                  // GOD-ADMIN ONLY: even if the backend accidentally leaked
                  // an admin-adjustment marker, isGod ensures a delegated
                  // admin never renders the ADMIN badge or the actor line.
                  const adminAdj = isGod && isAdminAdjustment(row);
                  const amtNum = Number(row.amount ?? 0);
                  const isCredit = amtNum >= 0;
                  const md = row.metadata || {};
                  // Patch v18.0 — counterparty pair for external user-to-user
                  // transfers. Backend enrichment (God-Admin only) adds
                  // counterparty_username + counterparty_role when the row
                  // has peer_transfer / pif / orphan_user_id metadata.
                  const cpUsername = isGod ? row.counterparty_username : null;
                  const cpRole = isGod ? row.counterparty_role : null;
                  const hasCounterparty = Boolean(cpUsername || row.counterparty_user_id);
                  return (
                    <tr key={row.transaction_id} data-testid={`audit-row-${row.transaction_id}`}>
                      <td className="text-xs text-[var(--sc-text-dim)] sc-tabular">{String(row.timestamp).slice(0, 19).replace('T', ' ')}</td>
                      <td className="text-xs">
                        {hasCounterparty ? (
                          // Render "<from_username> → <to_username>" for external
                          // user-to-user flows. The row.username is the primary
                          // party (the user_id of the ledger row); the counterparty
                          // is the other party. cpRole tells us which side the
                          // counterparty is on ("from" = payer, "to" = beneficiary).
                          <div className="flex items-center gap-1 flex-wrap">
                            <span className="font-semibold">
                              {cpRole === 'from' ? cpUsername || row.counterparty_user_id?.slice(0, 8) || '?' : row.username || row.user_id?.slice(0, 8)}
                            </span>
                            <span className="text-[10px] text-[var(--sc-text-mute)]">→</span>
                            <span className="font-semibold">
                              {cpRole === 'from' ? row.username || row.user_id?.slice(0, 8) : cpUsername || row.counterparty_user_id?.slice(0, 8) || '?'}
                            </span>
                          </div>
                        ) : (
                          <div className="font-semibold">{row.username || row.user_id}</div>
                        )}
                        <div className="text-[10px] text-[var(--sc-text-mute)]">{row.user_id}</div>
                        {adminAdj ? (
                          <div
                            className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9.5px] uppercase tracking-[0.16em] font-bold"
                            style={
                              row.actor_is_god
                                ? {
                                    background: 'rgba(255,194,14,0.14)',
                                    border: '1px solid rgba(255,194,14,0.55)',
                                    color: '#FFE08A',
                                  }
                                : {
                                    background: 'rgba(148,163,184,0.12)',
                                    border: '1px solid rgba(148,163,184,0.42)',
                                    color: '#cbd5e1',
                                  }
                            }
                            title={row.actor_is_god ? 'Adjustment performed by God Admin' : 'Adjustment performed by a delegated admin'}
                            data-testid={`audit-actor-${row.transaction_id}`}
                          >
                            {row.actor_is_god ? <Crown className="w-2.5 h-2.5" /> : <ShieldAlert className="w-2.5 h-2.5" />}
                            by {row.actor_username || md.actor_user_id || 'unknown'}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        {adminAdj ? (
                          <div className="flex flex-col gap-1">
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase w-fit"
                              style={{
                                background: 'rgba(255,194,14,0.14)',
                                border: '1px solid rgba(255,194,14,0.55)',
                                color: '#FFE08A',
                              }}
                            >
                              Admin
                            </span>
                            <span className="text-[9.5px] uppercase tracking-[0.16em] text-[var(--sc-text-mute)]">
                              {bucketLabel(md.bucket)} · {isCredit ? 'Credit' : 'Debit'}
                            </span>
                          </div>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase"
                            style={typeStyle(row.type)}
                          >
                            {row.type}
                          </span>
                        )}
                      </td>
                      <td className="text-right sc-tabular text-[#FFC20E]">
                        {adminAdj ? (
                          <span className="inline-flex items-center gap-1 justify-end">
                            {isCredit ? <ArrowUpRight className="w-3 h-3 text-emerald-300" /> : <ArrowDownRight className="w-3 h-3 text-rose-300" />}
                            {money(row.amount)}
                          </span>
                        ) : (
                          money(row.amount)
                        )}
                      </td>
                      <td className="text-[10px] text-[var(--sc-text-mute)] truncate max-w-[260px] font-mono">{row.hash?.slice(0, 24)}…</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <AdminPagination page={data.page} totalPages={data.total_pages} total={data.total} onChange={(p) => setPage(p)} />
      </div>
    </div>
  );
};

export default AuditSubpage;
